import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {APIClient} from '../../utils/APIClient';
import {API_URL} from '../../settings/local_settings';
import {FaIcon} from '../FaIcon';
import {copyPlainText} from '../../utils/copyPlainText';

const COPIED_MS = 2000;

/** La lista de prompts publicados, tolerante a las dos formas razonables de la respuesta
 *  (array pelado o `{prompts: [...]}`). Cualquier otra cosa —incluido un error de APIClient,
 *  que llega como objeto con `status`— se trata como «no hay nada que mostrar». */
export function normalizeDiscoveryPayload(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (data.status === 'ERROR' || data.status === 'NETWORK_ERROR') return [];
    if (Array.isArray(data.prompts)) return data.prompts;
    if (data.data && Array.isArray(data.data.prompts)) return data.data.prompts;
    if (Array.isArray(data.data)) return data.data;
    return [];
}

/** ¿La respuesta es un FALLO y no un «no hay prompts publicados»? Las dos se ven igual en pantalla
 *  (la sección no existe), y esa es justamente la razón por la que hay que poder distinguirlas en
 *  la consola: un 400 `MISSING_CLIENT` o un 500 no dejan ningún rastro visible. */
export function isDiscoveryFailure(data) {
    return Boolean(data && (data.status === 'ERROR' || data.status === 'NETWORK_ERROR'));
}

/**
 * «Lo que puedes pedirle a tu IA» — los prompts que la agencia publicó para este cliente,
 * como chips copiables en la pantalla del conector del portal.
 *
 * Datos: `GET agent/prompt-library/discovery/?client_id=<id>` — el token del usuario del portal
 * scopea a SUS clientes, y el `client_id` elige cuál de ellos. Es obligatorio pasarlo: sin él, un
 * usuario con varios clientes recibe los prompts del cliente primario (por id ascendente), que no
 * es el espacio que está mirando; y cuando se prenda `PORTAL_REQUIRE_EXPLICIT_CLIENT` en el
 * backend, el endpoint pasa a devolver 400 `MISSING_CLIENT` y la sección desaparecería sin que
 * nadie se entere. Si no hay nada publicado, la sección entera no existe: una lista vacía acá no
 * es un estado, es ruido. El click copia el TÍTULO al portapapeles — el cliente se lo pide a su IA
 * por nombre; el texto completo del prompt lo resuelve el conector.
 */
export function PortalPromptChips({clientId = null}) {
    const {t} = useTranslation();
    const [prompts, setPrompts] = useState([]);
    const [copiedKey, setCopiedKey] = useState(null);
    const timer = useRef(null);

    // El timeout del «¡Copiado!» se limpia al DESMONTAR, no cuando cambia el cliente: vive con el
    // componente, no con el fetch.
    useEffect(() => () => {
        if (timer.current) clearTimeout(timer.current);
    }, []);

    useEffect(() => {
        // Sin cliente resuelto no se pide nada. `usePortalClient` ya resuelve solo el caso de un
        // único cliente (deja su id en `activeClientId`), así que un null acá significa «todavía no
        // sé cuál»: multi-cliente sin elección hecha, o la lista sin cargar. Pedir sin `client_id`
        // ahí devolvería los prompts de un cliente que el usuario no eligió.
        if (!clientId) {
            setPrompts([]);
            return undefined;
        }
        let alive = true;
        const url = `${API_URL}agent/prompt-library/discovery/?client_id=${encodeURIComponent(clientId)}`;
        new APIClient(url).call('GET').then((data) => {
            if (!alive) return;
            if (isDiscoveryFailure(data)) {
                // La sección se sigue ocultando en pantalla —no hay nada útil que mostrarle al
                // cliente—, pero el fallo queda registrado para que sea diagnosticable.
                // eslint-disable-next-line no-console
                console.warn('[PortalPromptChips] no se pudo traer la biblioteca publicada', {
                    clientId,
                    status: data.status,
                    errorCode: data.error_code,
                    httpStatus: data.http_status,
                });
                setPrompts([]);
                return;
            }
            setPrompts(normalizeDiscoveryPayload(data));
        });
        return () => {
            alive = false;
        };
    }, [clientId]);

    const chips = prompts
        .map((prompt, index) => ({
            key: prompt.id !== undefined && prompt.id !== null ? String(prompt.id) : `i-${index}`,
            title: (prompt.title || '').trim(),
        }))
        .filter((chip) => chip.title);

    if (chips.length === 0) return null;

    const handleCopy = (chip) => {
        copyPlainText(chip.title).then(() => {
            setCopiedKey(chip.key);
            if (timer.current) clearTimeout(timer.current);
            timer.current = setTimeout(() => setCopiedKey(null), COPIED_MS);
        }).catch(() => { /* sin clipboard no hay feedback que dar: el chip queda como estaba */ });
    };

    return (
        <div className="dashbo-mcp-section portal-prompt-chips">
            <h2 className="section-title">{t('dashbo_mcp.portal_prompts.title')}</h2>
            <p className="section-description">{t('dashbo_mcp.portal_prompts.subtitle')}</p>
            <div className="ppc-chips">
                {chips.map((chip) => (
                    <button
                        key={chip.key}
                        type="button"
                        className={`ppc-chip ${copiedKey === chip.key ? 'is-copied' : ''}`}
                        onClick={() => handleCopy(chip)}
                        aria-label={t('dashbo_mcp.portal_prompts.copy_aria',
                            {title: chip.title, interpolation: {escapeValue: false}})}
                    >
                        <FaIcon iconName={copiedKey === chip.key ? 'check' : 'copy'}/>
                        <span>{copiedKey === chip.key ? t('dashbo_mcp.portal_prompts.copied') : chip.title}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default PortalPromptChips;
